import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { getRole, UserRole } from '@devd-client/api';

import { DetailsInitiatives } from '@devd-client/devd/deliverables';
import { ActivityLogProvider } from '@devd-client/devd/activity-log';
import { GoalsProvider } from '@devd-client/devd/goals';
import { WorkflowAutomationProvider } from '@devd-client/devd/workflow-automation';
import { InvestmentsProvider } from '@devd-client/devd/investments';
import {
  ActivityLog,
  Feedback,
  Goals,
  Home,
  Deliverables,
  Investments,
  Teams,
  Reports,
  ContributorDetails,
  Survey,
  DeploymentDetails,
  SprintDetails,
  Ticket,
  Git,
  Dora,
  Sprint,
  CreateInitiatives,
  EditInitiatives,
  HomeInsight,
  Quality,
  CustomDashboard,
  AddMetric,
  TeamCommparison,
  WorkflowAutomation,
  PrDetails,
  IssueDetails,
  Cicd,
  CicdDetails,
  Copilot,
  AiImpact,
  ContributorTable,
  TestSuite,
  TestSuiteDetails,
  SoftwareCapitalization,
  ScenarioPlanner,
  CreateGoal,
  EditGoal,
  WorkingAgreements,
  AiChat,
} from './asyncPages';
import { AskReports } from '@devd-client/devd/report';
import NotFound from '../Status/404NotFound';
import { LockIcon } from '@devd-client/devd/components';
import { DetailsTeamPage } from '@devd-client/devd/team-details';
import { Card, Center, Stack, Text } from '@chakra-ui/react';

export type RouteType = {
  id: string;
  path: string;
  label: string;
  icon: string;
  element: JSX.Element;
};

type Props = {
  appState: any;
};

export function Dashboard({ appState }: Props) {
  const location = useLocation();

  const disablePage = (route: RouteType) => {
    if (
      route.id === 'Feedback' &&
      !appState?.user?.dto?.modules?.cycleFeedback
    ) {
      return true;
    }

    if (
      (getRole() === UserRole.EMPLOYEE ||
        appState?.user?.dto?.modules?.contributorSearch === false) &&
      route.id === 'Contributors'
    ) {
      return true;
    }

    if (route.id === 'Teams' && appState?.user?.dto?.modules?.teams === false) {
      return true;
    }

    if (
      route.id === 'Goals' &&
      appState?.user?.dto?.modules?.agreements === false
    ) {
      return true;
    }

    if (
      route.id === 'Investments' &&
      appState?.user?.dto?.modules?.investment === false
    ) {
      return true;
    }

    if (
      route.id === 'ActivityLog' &&
      appState?.user?.dto?.modules?.activityLog === false
    ) {
      return true;
    }

    if (
      route.id === 'SoftwareCapitalization' &&
      appState?.user?.dto?.modules?.capitalization === false
    ) {
      return true;
    }

    return false;
  };

  const routes: RouteType[] = useMemo(() => {
    return [
      {
        id: 'overview',
        path: 'dashboard/overview',
        label: 'dashboard',
        icon: 'dashboard',
        element: <Home />,
      },
      {
        id: 'Home',
        path: 'home',
        label: 'home',
        icon: 'home',
        element: <HomeInsight />,
      },
      {
        id: 'Ticket',
        path: 'dashboard/ticket',
        label: 'Ticket',
        icon: 'Ticket',
        element: <Ticket />,
      },
      {
        id: 'Git',
        path: 'dashboard/git',
        label: 'Git',
        icon: 'Git',
        element: <Git />,
      },
      {
        id: 'Dora',
        path: 'dashboard/dora',
        label: 'Dora',
        icon: 'Dora',
        element: <Dora />,
      },
      {
        id: 'Sprint',
        path: 'sprint',
        label: 'Sprint',
        icon: 'Sprint',
        element: <Sprint />,
      },
      {
        id: 'Quality',
        path: 'dashboard/quality',
        label: 'Quality',
        icon: 'Quality',
        element: <Quality />,
      },
      {
        id: 'Cicd',
        path: 'dashboard/cicd',
        label: 'CI/CD',
        icon: 'CICD',
        element: <Cicd />,
      },
      {
        id: 'AI Adoption',
        path: 'dashboard/ai-adoption',
        label: 'AI Adoption',
        icon: 'AI Adoption',
        element: <Copilot />,
      },
      {
        id: 'AI Impact',
        path: 'dashboard/ai-impact',
        label: 'AI Impact',
        icon: 'AI Impact',
        element: <AiImpact />,
      },
      {
        id: 'testSuite',
        path: 'dashboard/test-suite',
        label: 'Test Suite',
        icon: 'TestSuite',
        element: <TestSuite />,
      },
      {
        id: 'Custom Dashboard',
        path: 'dashboard/custom-dashboard',
        label: 'Custom Dashboard',
        icon: 'Custom',
        element: <CustomDashboard />,
      },
      {
        id: 'Deliverables',
        path: 'deliverables',
        label: 'Deliverables',
        icon: 'Deliverables',
        element: <Deliverables />,
      },
      {
        id: 'Deliverables',
        path: 'deliverables/:deliverableId',
        label: 'Deliverables Details',
        icon: 'Deliverables',
        element: <DetailsInitiatives />,
      },
      {
        id: 'Deliverables',
        path: 'deliverables/create',
        label: 'Create Initiatives',
        icon: 'Initiatives',
        element: <CreateInitiatives />,
      },
      {
        id: 'Deliverables',
        path: 'deliverables/edit',
        label: 'Edit Initiatives',
        icon: 'Initiatives',
        element: <EditInitiatives />,
      },
      {
        id: 'Deliverables',
        path: 'deliverables/scenario-planner',
        label: 'Scenario Planner',
        icon: 'Initiatives',
        element: <ScenarioPlanner />,
      },
      {
        id: 'Investments',
        path: 'investments',
        label: 'Investments',
        icon: 'Initiatives',
        element: (
          <InvestmentsProvider>
            <Investments />
          </InvestmentsProvider>
        ),
      },
      {
        id: 'Feedback',
        path: 'feedback',
        label: 'Feedback',
        icon: 'Feedback',
        element: <Feedback />,
      },
      {
        id: 'AiChat',
        path: 'dyna-ai/:sessionId?',
        label: 'Dyna AI',
        icon: 'AiChat',
        element: <AiChat />,
      },
      {
        id: 'Reports',
        path: 'reports',
        label: 'reports',
        icon: 'reports',
        element: <Reports />,
      },
      {
        id: 'ActivityLog',
        path: 'activity-log',
        label: 'Activity log',
        icon: 'ActivityLog',
        element: (
          <ActivityLogProvider>
            <ActivityLog />
          </ActivityLogProvider>
        ),
      },
      {
        id: 'Goals',
        path: 'goals',
        label: 'Goals',
        icon: 'Goals',
        element: (
          <GoalsProvider>
            <WorkingAgreements />
          </GoalsProvider>
        ),
      },
      {
        id: 'Goals',
        path: 'goal',
        label: 'Goals',
        icon: 'Goals',
        element: (
          <GoalsProvider>
            <Goals />
          </GoalsProvider>
        ),
      },
      {
        id: 'Goals',
        path: 'goal/create',
        label: 'Create Goals',
        icon: 'Create Goals',
        element: <CreateGoal />,
      },
      {
        id: 'Goals',
        path: 'goal/edit/:goalId',
        label: 'Edit Goal',
        icon: 'Edit Goal',
        element: <EditGoal />,
      },
      {
        id: 'Automations',
        path: 'workflow-automation',
        label: 'Workflow Automation',
        icon: 'Goals',
        element: (
          <WorkflowAutomationProvider>
            <WorkflowAutomation />
          </WorkflowAutomationProvider>
        ),
      },
      {
        id: 'Teams',
        path: 'teams',
        label: 'Teams',
        icon: 'Teams',
        element: <Teams />,
      },
      {
        id: 'Pull Requests',
        path: 'pr-details',
        label: 'Pr-details',
        icon: '',
        element: <PrDetails />,
      },
      {
        id: 'Issues',
        path: 'issue-details',
        label: 'Issue-details',
        icon: '',
        element: <IssueDetails />,
      },
      {
        id: 'Teams',
        path: 'teams/:teamId',
        label: 'Team Details',
        icon: 'Teams',
        element: <DetailsTeamPage />,
      },
      {
        id: 'Compare Teams',
        path: 'teams/comparison',
        label: 'Teams Comparison',
        icon: 'Teams',
        element: <TeamCommparison />,
      },
      {
        id: 'Compare Teams',
        path: 'compare-teams',
        label: 'Teams Comparison',
        icon: 'Teams',
        element: <TeamCommparison />,
      },
      {
        id: 'Contributors',
        path: 'contributors',
        label: 'Contributor',
        icon: '',
        element: <ContributorTable />,
      },
      {
        id: 'ContributorId',
        path: 'contributors/:contributorId',
        label: 'Contributor Details',
        icon: '',
        element: <ContributorDetails />,
      },
      {
        id: 'Survey',
        path: 'surveys',
        label: 'Survey',
        icon: 'Survey',
        element: <Survey />,
      },
      {
        id: 'SoftwareCapitalization',
        path: 'software-capitalization',
        label: 'Software Capitalization',
        icon: 'Software Capitalization',
        element: <SoftwareCapitalization />,
      },
      {
        id: 'NotFound',
        path: '*',
        label: '',
        icon: '',
        element: <NotFound />,
      },
    ];
  }, []);

  return (
    <Routes key={location.pathname}>
      <Route
        path="/dashboard"
        element={<Navigate to={'/dashboard/git'} replace />}
      />
      {routes.map((route, index) => (
        <Route
          key={index}
          path={`/` + route.path}
          element={
            getRole() === UserRole.MANAGER &&
            ['investments'].includes(route.path) ? (
              <Center height="100%">
                <Card>
                  <Stack spacing={4} mb={2}>
                    <Center>
                      <LockIcon height={'80px'} width="300px" />
                    </Center>

                    <Text
                      fontFamily="heading"
                      fontSize="xl"
                      fontWeight="medium"
                      color="text.secondary"
                      alignSelf="center"
                    >
                      You don't have access.
                    </Text>
                  </Stack>
                </Card>
              </Center>
            ) : getRole() !== UserRole.EMPLOYER_ADMIN &&
              getRole() !== UserRole.SUPPORT &&
              ['goal/create', 'goal/edit/:goalId'].includes(route.path) ? (
              <Center height="100%">
                <Card>
                  <Stack spacing={4} mb={2}>
                    <Center>
                      <LockIcon height={'80px'} width="300px" />
                    </Center>

                    <Text
                      fontFamily="heading"
                      fontSize="xl"
                      fontWeight="medium"
                      color="text.secondary"
                      alignSelf="center"
                    >
                      You don't have access.
                    </Text>
                  </Stack>
                </Card>
              </Center>
            ) : getRole() === UserRole.EMPLOYEE && route.path === 'home' ? (
              <Navigate to={`/contributors/${appState?.user?.email}`} />
            ) : (getRole() === UserRole.EMPLOYEE &&
                (route.id === 'Deliverables' ||
                  route.id === 'Feedback' ||
                  route.id === 'Reports' ||
                  route.id === 'Survey' ||
                  route.id === 'Investments' ||
                  route.id === 'ActivityLog' ||
                  route.id === 'Compare Teams' ||
                  route.id === 'AiChat')) ||
              disablePage(route) ? (
              <Center height="100%">
                <Card>
                  <Stack spacing={4} mb={2}>
                    <Center>
                      <LockIcon height={'80px'} width="300px" />
                    </Center>

                    <Text
                      fontFamily="heading"
                      fontSize="xl"
                      fontWeight="medium"
                      color="text.secondary"
                      alignSelf="center"
                    >
                      You don't have access.
                    </Text>
                  </Stack>
                </Card>
              </Center>
            ) : (
              route.element
            )
          }
        />
      ))}

      <Route path="/reports/ask-question" element={<AskReports />} />
      <Route path=":params/sprint/:id" element={<SprintDetails />} />
      <Route path="/sprint/:id" element={<SprintDetails />} />
      <Route path="/timeline/:id/:id" element={<DeploymentDetails />} />
      <Route
        path="/dashboard/custom-dashboard/add-metric"
        element={<AddMetric />}
      />
      <Route
        path="/dashboard/custom-dashboard/edit-metric"
        element={<AddMetric />}
      />
      <Route path="/teams/:teamId/add-metric" element={<AddMetric />} />
      <Route path="/dashboard/cicd/:name" element={<CicdDetails />} />
      <Route
        path="/dashboard/test-suite/:name"
        element={<TestSuiteDetails />}
      />
    </Routes>
  );
}

export default Dashboard;
